import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import {
  ButtonUI,
  Input,
  ModalUI,
  Selector,
  type SelectorOptionPropsInterface,
  showNotification,
  type ValidationErrors
} from 'sonata-react-components';
import { createCampaignPlan, editCampaignPlan } from 'sonata-static-storage/lib/entities';
import { iabCategoriesDictionary } from 'sonata-static-storage/lib/samples/iabCategories.samples';

import { useCampaignPlan } from '@/context/CampaignPlanProvider';
import { countriesOptions, getCountriesValuesOptions } from '@/helpers/countriesOptions/countriesOptions.helper.ts';
import { useModals } from '@/router';
import { useStore as useCampaignStore } from '@/store/local/campaigns';
import { CampaignPlanFormStyled } from './CampaignPlanForm.styles';

import type { CampaignPlan } from 'sonata-static-storage/lib/schemas/multiFlight/campaignPlan.schema';
import type { CampaignsStore } from '@/store/local/campaigns';

type State = {
  errors?: ValidationErrors;
};

export default function CampaignPlanForm() {
  const location = useLocation();
  const { formatMessage } = useIntl();
  const {
    _id,
    countries,
    errors: validationErrors,
    iabCategories,
    mediaAdvertiserUser,
    name,
    setCountries,
    setIabCategories,
    setName,
    userId
  } = useCampaignPlan();
  const modals = useModals();
  const modalRef: any = useRef();
  const createToken = useRef({} as Record<string, any>);
  const { setCacheSeed, setHasCampaigns } = useCampaignStore() as CampaignsStore;

  const [{ errors }, setState] = useState<State>({});

  useEffect(() => {
    modalRef?.current?.openModal();
  }, [modalRef]);

  useEffect(() => {
    return () => {
      createToken.current?.cancel?.();
    };
  }, []);

  const countryValuesOptions = getCountriesValuesOptions(countries);

  const campaignCategoryOptions: SelectorOptionPropsInterface[] = iabCategoriesDictionary.reduce(
    (acc: SelectorOptionPropsInterface[], iabCategory) => {
      return [
        ...acc,
        {
          id: iabCategory.categoryId,
          label: iabCategory.categoryName
        }
      ];
    },
    [
      {
        id: 'noCategory',
        label: formatMessage({
          defaultMessage: 'No Category',
          id: 'campaignPlans.modal.noCategory'
        })
      }
    ]
  );

  const getCampaignCategoryName = (categoryId: string) => {
    const category = iabCategoriesDictionary.find(iabCategory => iabCategory.categoryId === categoryId)!;
    return category.categoryName;
  };

  const handleCountryChange = (options: SelectorOptionPropsInterface[]) => {
    setCountries(options.map(option => option.id as string));
  };

  const handleCampaignNameChange = (text: string | FileList | null) => {
    setName(text as string);
  };

  const handleCampaignCategoryChange = (option: SelectorOptionPropsInterface) => {
    setIabCategories(option.id === 'noCategory' ? [] : [option.id as string]);
  };

  const isEdition = Boolean(_id);

  const areValidationsOk = (): boolean => {
    setState(prev => ({
      ...prev,
      errors: validationErrors
    }));

    return isEmpty(validationErrors);
  };

  const handleCreate = async (): Promise<CampaignPlan | undefined> => {
    const cp = {
      completed: true,
      countries,
      iabCategories,
      name,
      ...(mediaAdvertiserUser && { mediaAdvertiserUser })
    };

    try {
      const newCp = await (isEdition
        ? editCampaignPlan(userId, _id as string, cp, createToken.current)
        : createCampaignPlan(userId, cp, createToken.current));
      setCacheSeed();
      showNotification(
        'notify',
        formatMessage({
          defaultMessage: 'Campaign Plan created successfully',
          id: 'campaignPlans.notifications.created'
        })
      );
      setHasCampaigns(true);
      return newCp;
    } catch (_e) {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Failure creating the Campaign Plan',
          id: 'campaignPlans.notifications.notCreated'
        })
      );
    }
  };

  const handleClose = (toCampaigns?: boolean) =>
    modals.close({
      at: {
        pathname: '/campaigns',
        search: toCampaigns ? 'tab=campaignPlan' : location.search
      }
    });

  const handleCreateCampaignPlan = async () => {
    if (!areValidationsOk()) {
      return;
    }
    await handleCreate();
    handleClose(true);
  };

  const handleCreateIO = async () => {
    if (!areValidationsOk()) {
      return;
    }
    const cp = await handleCreate();
    modals.open('/campaigns/new/insertionOrderModal', {
      at: {
        pathname: '/campaigns',
        search: location.search
      },
      state: {
        campaignPlan: cp,
        modalId: 'insertionOrder-modal'
      }
    });
  };
  return (
    <CampaignPlanFormStyled>
      <ModalUI
        closeModalCallback={handleClose}
        ref={modalRef}
        rightButton={
          <>
            <ButtonUI
              icon="fa fa-plus"
              id="createCampaignBtn"
              onClick={handleCreateCampaignPlan}
              shape="squareRounded"
              text={formatMessage({
                defaultMessage: `${isEdition ? 'Update' : 'Create'} Campaign Plan`,
                id: isEdition ? 'campaignPlans.modal.editButton' : 'campaignPlans.modal.createButton'
              })}
              type="primary"
            />
            <ButtonUI
              icon="fa fa-arrow-right"
              iconPosition="right"
              id="ioSettingsBtn"
              onClick={handleCreateIO}
              shape="squareRounded"
              text={formatMessage({
                defaultMessage: 'Insertion Order Settings',
                id: 'campaignPlans.modal.ioButton'
              })}
              type="secondary"
            />
          </>
        }
        title={formatMessage({
          defaultMessage: `${isEdition ? 'Update' : 'Create'} Campaign Plan`,
          id: `campaignPlans.modal.title.${isEdition ? 'edit' : 'create'}`
        })}
        width="849px"
      >
        <div className="campaignPlanModal">
          <Input
            description={
              errors?.name
                ? formatMessage({
                    defaultMessage: 'Campaign Plan name should have at least 3 characters',
                    id: 'campaignPlans.modal.name.error'
                  })
                : undefined
            }
            id="campaignPlanModal-campaignName"
            inputStatus={errors?.name ? 'wrong' : 'default'}
            label={formatMessage({
              defaultMessage: 'Campaign Plan name',
              id: 'campaignPlans.modal.name.label'
            })}
            name="campaignPlanModal-campaignName"
            onChange={handleCampaignNameChange}
            placeholder={formatMessage({
              defaultMessage: 'Campaign Plan name',
              id: 'campaignPlans.modal.name.placeholder'
            })}
            required
            shape="squareUI"
            type="text"
            value={name}
          />
          <div className="campaignPlanModal__row">
            <Selector
              className="campaignPlanModal__rowCountry"
              defaultValue={countryValuesOptions}
              description={
                errors?.countries
                  ? formatMessage({
                      defaultMessage: 'At least one country must be selected',
                      id: 'campaignPlans.modal.countryError'
                    })
                  : undefined
              }
              label={formatMessage({
                defaultMessage: 'Country',
                id: 'campaignPlans.modal.country'
              })}
              onChange={handleCountryChange}
              options={countriesOptions}
              required
              selectorStatus={errors?.countries ? 'wrong' : 'default'}
              type="multiple"
            />
            <Selector
              className="campaignPlanModal__rowCategory"
              description={
                errors?.iabCategories
                  ? formatMessage({
                      defaultMessage: 'A category is required',
                      id: 'campaignPlans.modal.campaignCategoryError'
                    })
                  : undefined
              }
              label={formatMessage({
                defaultMessage: 'Campaign Plan Category',
                id: 'campaignPlans.modal.campaignCategory'
              })}
              onChange={handleCampaignCategoryChange}
              options={campaignCategoryOptions}
              required
              selectorStatus={errors?.iabCategories ? 'wrong' : 'default'}
              type="simple"
              value={
                isEmpty(iabCategories)
                  ? [
                      {
                        id: 'noCategory',
                        label: formatMessage({
                          defaultMessage: 'No Category',
                          id: 'campaignPlans.modal.noCategory'
                        })
                      }
                    ]
                  : [
                      {
                        id: iabCategories[0],
                        label: getCampaignCategoryName(iabCategories[0])
                      }
                    ]
              }
            />
          </div>
        </div>
      </ModalUI>
    </CampaignPlanFormStyled>
  );
}
